import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { Typography, Card, Row, Col, Button, Spin, message } from 'antd';
import {
  FileExcelOutlined,
  SwapOutlined,
  WalletOutlined,
  UserOutlined,
  ShopOutlined,
  TagOutlined,
  BarChartOutlined,
} from '@ant-design/icons';
import Layout from '../../components/AdminLayout';
import { auth } from '@festpix/services';
import { colors } from '@festpix/ui';
import {
  exportTransactions,
  exportWallets,
  exportUsers,
  exportBooths,
  exportSKUs,
  exportSKUSalesCounters,
} from '../../services/exportService';

const { Title, Text } = Typography;

interface ReportCard {
  key: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  exportFn: () => Promise<void>;
}

const reportCards: ReportCard[] = [
  {
    key: 'transactions',
    title: 'Transações',
    description: 'Exportar todas as transações do sistema incluindo compras, recargas e transferências.',
    icon: <SwapOutlined style={{ fontSize: 32, color: colors.primary }} />,
    exportFn: exportTransactions,
  },
  {
    key: 'wallets',
    title: 'Carteiras',
    description: 'Exportar todas as carteiras de participantes com saldos e informações.',
    icon: <WalletOutlined style={{ fontSize: 32, color: colors.primary }} />,
    exportFn: exportWallets,
  },
  {
    key: 'users',
    title: 'Usuários',
    description: 'Exportar lista de usuários do sistema com seus perfis e carteiras.',
    icon: <UserOutlined style={{ fontSize: 32, color: colors.primary }} />,
    exportFn: exportUsers,
  },
  {
    key: 'booths',
    title: 'Estandes',
    description: 'Exportar lista de estandes com seus saldos e vendedores associados.',
    icon: <ShopOutlined style={{ fontSize: 32, color: colors.primary }} />,
    exportFn: exportBooths,
  },
  {
    key: 'skus',
    title: 'SKUs',
    description: 'Exportar todos os produtos (SKUs) com preços, quantidades e estandes.',
    icon: <TagOutlined style={{ fontSize: 32, color: colors.primary }} />,
    exportFn: exportSKUs,
  },
  {
    key: 'sales_counters',
    title: 'Contadores de Vendas',
    description: 'Exportar contadores de vendas por SKU para análise de desempenho.',
    icon: <BarChartOutlined style={{ fontSize: 32, color: colors.primary }} />,
    exportFn: exportSKUSalesCounters,
  },
];

export default function ReportsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState<string | null>(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) {
        navigate('/admin/login');
      } else {
        setLoading(false);
      }
    });
    return unsubscribe;
  }, [navigate]);

  const handleExport = async (report: ReportCard) => {
    setExporting(report.key);
    try {
      await report.exportFn();
      message.success(`${report.title} exportado com sucesso!`);
    } catch (error) {
      console.error(`Error exporting ${report.key}:`, error);
      message.error(`Erro ao exportar ${report.title}. Tente novamente.`);
    } finally {
      setExporting(null);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div style={{ display: 'flex', justifyContent: 'center', padding: 100 }}>
          <Spin size="large" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <Title level={2} style={{ marginBottom: 8 }}>
        <FileExcelOutlined style={{ marginRight: 12 }} />
        Relatórios
      </Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
        Exporte dados do sistema em formato Excel (.xlsx)
      </Text>

      <Row gutter={[24, 24]}>
        {reportCards.map((report) => (
          <Col xs={24} sm={12} lg={8} key={report.key}>
            <Card
              style={{
                height: '100%',
                background: colors.cardBackground,
                borderRadius: 12,
              }}
              hoverable
            >
              <div style={{ textAlign: 'center', marginBottom: 16 }}>
                {report.icon}
              </div>
              <Title level={4} style={{ textAlign: 'center', marginBottom: 8 }}>
                {report.title}
              </Title>
              <Text
                type="secondary"
                style={{
                  display: 'block',
                  textAlign: 'center',
                  marginBottom: 20,
                  minHeight: 44,
                }}
              >
                {report.description}
              </Text>
              <Button
                type="primary"
                icon={<FileExcelOutlined />}
                block
                size="large"
                loading={exporting === report.key}
                disabled={exporting !== null && exporting !== report.key}
                onClick={() => handleExport(report)}
                style={{
                  background: colors.success,
                  borderColor: colors.success,
                }}
              >
                Baixar Excel
              </Button>
            </Card>
          </Col>
        ))}
      </Row>
    </Layout>
  );
}
